import { useEffect, useRef, useState } from "react";
import type { CapWidget, CapSolveEvent } from "cap-widget";
import { useI18n } from "../../i18n";
import type { Challenge } from "../client";
import { registerCapTransport } from "./transport";
import { Notice } from "../../components/ui";
export default function CapVerification({
  challenge,
  onSolved,
}: {
  challenge: Challenge;
  onSolved: (pass: string) => void;
}) {
  const { t, locale } = useI18n();
  const host = useRef<HTMLDivElement>(null);
  const solved = useRef(onSolved);
  solved.current = onSolved;
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let disposed = false;
    let widget: CapWidget | undefined;
    const transport = registerCapTransport(challenge);
    const success = (event: CapSolveEvent) => {
      if (!disposed && typeof event.detail.token === "string")
        solved.current(event.detail.token);
    };
    const error = () => {
      if (!disposed) setFailed(true);
    };
    void import("virtual:forma-cap-widget")
      .then(() => {
        if (disposed) return;
        widget = document.createElement("cap-widget");
        const labels: Record<string, string> = {
          "initial-state": t("shield.cap.initial"),
          "verifying-label": t("shield.verifying"),
          "solved-label": t("shield.cap.solved"),
          "error-label": t("shield.verifyFailed"),
          "verify-aria-label": t("shield.verifyStart"),
          "verifying-aria-label": t("shield.verifying"),
          "verified-aria-label": t("shield.cap.solved"),
          "error-aria-label": t("shield.verifyFailed"),
          "required-label": t("shield.cap.required"),
          "troubleshooting-label": t("nav.support"),
          "wasm-disabled": t("shield.cap.required"),
        };
        Object.entries(labels).forEach(([key, value]) =>
          widget!.setAttribute("data-cap-i18n-" + key, value),
        );
        widget.setAttribute("data-cap-api-endpoint", transport.endpoint);
        widget.setAttribute("data-cap-worker-count", "2");
        widget.setAttribute("data-cap-disable-haptics", "");
        widget.setAttribute("data-cap-troubleshooting-url", "/support");
        widget.addEventListener("solve", success);
        widget.addEventListener("error", error);
        host.current?.replaceChildren(widget);
      })
      .catch(error);
    return () => {
      disposed = true;
      widget?.removeEventListener("solve", success);
      widget?.removeEventListener("error", error);
      widget?.remove();
      transport.dispose();
    };
  }, [challenge, locale, t]);
  return (
    <div className="cap-verification">
      <div ref={host} />
      {failed && <Notice tone="error">{t("shield.verifyFailed")}</Notice>}
    </div>
  );
}
